import { feedbackAudioUrl, isCorrectFeedback } from '../../mini-tasks/feedbackAudio';
import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { fetchTurkishL1Catalog, fetchTurkishL1Preview } from '../api/miniTasks';
import TurkishL1TourView from '../../mini-tasks/sound-hunt/TurkishL1TourView';
import RoundSummary from '../../mini-tasks/components/RoundSummary';
import SingleStoreyA from '../../mini-tasks/sound-hunt/SingleStoreyA';
import SchoolN from '../../mini-tasks/sound-hunt/SchoolN';
import LetterSequence from '../../mini-tasks/sound-hunt/LetterSequence';
import Grade1Letter from '../../mini-tasks/sound-hunt/Grade1Letter';


function HandwritingG12QaDetails({ round }) {
  const game = round.qa?.games?.find((item) => item.assessment_mode === 'handwriting_dictation');
  if (!game) return <Alert severity="warning">Duy ve Yaz içerik verisi bulunamadı.</Alert>;
  const clips = Object.fromEntries((game.qa_audio || []).map((clip) => [clip.id, clip]));
  return <Box sx={{ maxWidth: 920, mx: 'auto', my: 2 }}>
    <Box sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 4,
      background: 'linear-gradient(125deg, #f1e8ff 0%, #fff6e9 100%)',
      border: '1px solid #d7bff3' }}>
      <Typography sx={{ fontWeight: 900, color: '#65329b', fontSize: 13, letterSpacing: '.04em' }}>✏️ LEVEL {round.level} · TUR {round.tour} · G12</Typography>
      <Typography variant="h5" sx={{ color: '#48207b', fontWeight: 900, mt: 0.5 }}>Duy ve Yaz · Tomris Hedefleri</Typography>
      <Typography sx={{ color: '#514365', mt: 1 }}>Dinlenen iki hece veya sözcük yazılır. Bu ekran yalnız içerik ve ses incelemesidir.</Typography>
    </Box>
    <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
      Bu ekran personel önizlemesidir. Bu inceleme öğrenci sonucu, puan veya yayın onayı kaydetmez.
    </Alert>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
      {game.items.map((item, index) => {
        const clip = clips[item.audio_id];
        return <Paper key={item.id} elevation={0} sx={{ minWidth: 0, p: { xs: 2, sm: 2.5 }, borderRadius: 4,
          border: '2px solid #e2d0f5', background: '#fffdf9' }}>
          <Typography sx={{ fontWeight: 800, color: '#6d45df', fontSize: 13 }}>{index + 1}. {item.kind === 'syllable' ? 'HECE' : item.kind === 'syllable_word' ? 'HECE / SÖZCÜK' : 'SÖZCÜK'}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '.04em', color: '#48207b', my: 1 }}>
            {item.expected_word.toLocaleUpperCase('tr-TR')}
          </Typography>
          <Typography sx={{ fontWeight: 800, color: clip?.qa_status === 'approved' ? '#386122' : '#805319', mb: 1.5 }}>
            {clip?.qa_status === 'approved' ? '✓ Tomris · Ses onaylı' : 'Ses onayı bekleniyor'}
          </Typography>
          {game.audio?.[item.audio_id] ? <audio controls preload="none" aria-label={`${item.expected_word} Tomris sesini dinle`}
            src={game.audio[item.audio_id]} style={{ display: 'block', maxWidth: '100%', width: '100%' }}
            onPlay={(event) => {
              const active = event.currentTarget;
              active.closest('section')?.querySelectorAll('audio').forEach((other) => { if (other !== active) other.pause(); });
            }} /> : <Alert severity="warning">Ses kaydı bulunamadı.</Alert>}
          <Typography sx={{ fontSize: 12, mt: 1.5, color: '#675775' }}>Hedef, öğrencinin oyununda yazılı gösterilmez.</Typography>
        </Paper>;
      })}
    </Box>
    <Box component="details" sx={{ mt: 2, p: 2, border: '1px solid #d8c4e8', borderRadius: 3, bgcolor: '#fff' }}>
      <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 900, minHeight: 44, display: 'flex', alignItems: 'center', color: '#5424a1' }}>
        Teknik ayrıntılar · içerik ve medya doğrulaması
      </Box>
      <Typography sx={{ fontSize: 13, color: '#675775', my: 1 }}>İçerik sürümü: {round.content_version}</Typography>
      {(game.qa_audio || []).map((clip) => <Box key={clip.id} sx={{ p: 1.5, my: 1, borderRadius: 2, bgcolor: '#f8f5fc',
        overflowWrap: 'anywhere', fontSize: 12 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{clip.id} · Ses durumu: {clip.qa_status}</Typography>
        <Typography sx={{ fontSize: 12 }}>S3: {clip.s3_key}</Typography>
        <Typography sx={{ fontSize: 12 }}>SHA-256: {clip.sha256}</Typography>
        <Typography sx={{ fontSize: 12 }}>İnceleme: {clip.qa_review_id} · {clip.qa_approved_on}</Typography>
      </Box>)}
      {(round.qa.validators || []).map((check) => <Typography key={check.id} sx={{ fontSize: 12, overflowWrap: 'anywhere', my: 0.5 }}>
        {check.id}: {check.status}
      </Typography>)}
    </Box>
  </Box>;
}

export default function TurkishL1QaPanel({ schoolId, directPreview, onExitPreview }) {
  const [catalog, setCatalog] = useState(null);
  const [level, setLevel] = useState(1);
  const [tour, setTour] = useState(1);
  const [round, setRound] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const directHandled = useRef(null);
  const requestId = useRef(0);
  useEffect(() => {
    let alive = true;
    requestId.current += 1;
    setCatalog(null); setError(''); setRound(null); setSummary(null); setBusy(false);
    if (!schoolId) return undefined;
    fetchTurkishL1Catalog(schoolId).then((data) => {
      if (alive) setCatalog(data);
    }).catch((err) => { if (alive) setError(err.message); });
    return () => { alive = false; requestId.current += 1; };
  }, [schoolId]);
  useEffect(() => {
    if (!directPreview) { directHandled.current = null; return undefined; }
    if (!catalog || !schoolId) return undefined;
    const key = `${schoolId}:${directPreview.key}`;
    if (directHandled.current === key) return undefined;
    directHandled.current = key;
    if (directPreview.error) { setError(directPreview.error); return undefined; }
    const selected = catalog.levels?.find(entry => entry.level === directPreview.level);
    if (catalog.preview_id !== 61 || !selected?.ready_tours?.includes(directPreview.tour)) {
      setError('Bu level ve tur için önizleme bulunamadı.'); return undefined;
    }
    setLevel(directPreview.level); setTour(directPreview.tour); setError(''); setBusy(true);
    const current = ++requestId.current;
    fetchTurkishL1Preview(schoolId, 'preview', directPreview.tour, directPreview.level).then(data => {
      if (requestId.current === current) { setRound(data); setSummary(null); }
    }).catch(err => { if (requestId.current === current) setError(err.message); })
      .finally(() => { if (requestId.current === current) setBusy(false); });
    return undefined;
  }, [catalog, schoolId, directPreview]);
  const open = async (mode) => {
    const current = ++requestId.current;
    setBusy(true); setError('');
    try {
      const data = await fetchTurkishL1Preview(schoolId, mode, tour, ...(level === 1 ? [] : [level]));
      if (requestId.current === current) { setRound(data); setSummary(null); }
    }
    catch (err) { if (requestId.current === current) setError(err.message); }
    finally { if (requestId.current === current) setBusy(false); }
  };
  const exit = () => { requestId.current += 1; setRound(null); setSummary(null); setBusy(false); onExitPreview?.(); };
  const selectedLevel = catalog?.levels?.find(entry => entry.level === level);
  const handwritingTour = selectedLevel?.handwriting_preview?.tour || (level === 2 ? 7 : null);
  const handwriting = tour === handwritingTour;
  const handwritingReady = selectedLevel?.handwriting_preview?.ready !== false;
  const published = (catalog?.levels?.find((entry) => entry.level === level)?.content_status
    || (level === 1 ? catalog?.content_status : null)) === 'PUBLISHED';
  // Cover the existing picker so the preview uses the same full-screen game layout.
  if (summary) return <Box sx={{ position: 'fixed', inset: 0, zIndex: 1200, overflowY: 'auto' }}>
    <RoundSummary summary={summary} onBackToBoard={exit} />
  </Box>;
  if (round?.mode === 'preview') return <Box sx={{ position: 'fixed', inset: 0, zIndex: 1200, overflowY: 'auto', bgcolor: '#fffaf0' }}>
    <TurkishL1TourView round={round} onComplete={setSummary} onExit={exit} />
  </Box>;
  return <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 4, border: '2px solid #ddd6fe',
    '& .MuiButton-root': { minHeight: 44, minWidth: 44, '&:focus-visible': { outline: '3px solid #5b21b6', outlineOffset: 3 } } }}>
    <Typography variant="h5" sx={{ fontWeight: 900 }}>Türkçe Yolculuğu · 1. sınıf · {handwriting ? `L${level} G12 · Duy ve Yaz · Personel önizlemesi` : level === 6 ? 'Geçmişten Karma Tekrar' : level === 5 ? 'L5 v1.1 · İlk Sözcük Maceram' : level === 4 ? 'L4 v2.1 · i / l' : level === 3 ? 'L1 + L2 Karma Tekrar' : level === 1 ? 'L1 v2.2' : 'L2 v1.1'}</Typography>
    <Typography sx={{ my: 1 }}>Preview 61 · {handwriting ? 'Personel önizlemesi · Öğrenci sonucu ve puan kaydetmez.' : (
      (level === 1 && tour === 6) ||
      ([2, 4, 5].includes(level) && tour === catalog?.levels?.find(entry => entry.level === level)?.balloon_finale?.final_tour)
    ) ? (
      (level === 1 ? catalog?.balloon_finale?.new_student_release_enabled
        : published && catalog?.levels?.find(entry => entry.level === level)?.balloon_finale?.student_release_configured)
        ? `Balon Patlat · Yeni L${level} öğrencileri için yayında.`
        : 'Balon Patlat · Personel önizlemesi; öğrenci yayını kapalı.'
    ) : published ? 'Öğrenci yayını açık.' : 'Taslak · Öğrenci yayını kapalı.'}</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ my: 2 }}>
      {catalog?.levels && <TextField select disabled={busy} label="Level" value={level} onChange={(e) => {
        const nextLevel = Number(e.target.value);
        setLevel(nextLevel); setTour(catalog.levels.find(entry => entry.level === nextLevel)?.ready_tours?.[0] || 1); setRound(null);
      }} sx={{ minWidth: 120 }}>
        {catalog.levels.map((entry) => <MenuItem key={entry.level} value={entry.level} sx={{ minHeight: { xs: 44, sm: 44 } }}>{entry.level === 3 ? 'L1 + L2 Karma' : entry.level === 6 ? 'L6 · Geçmişten Karma' : `L${entry.level}`}</MenuItem>)}
      </TextField>}
      <TextField select disabled={busy} label="Tur" value={tour} onChange={(e) => { setTour(Number(e.target.value)); setRound(null); }} sx={{ minWidth: { xs: 0, sm: 240 } }}>
        {(catalog?.levels?.find((entry) => entry.level === level)?.ready_tours || catalog?.ready_tours || [1]).map((number) => <MenuItem key={number} value={number} sx={{ minHeight: { xs: 44, sm: 44 } }}>
          {number === handwritingTour ? `Tur ${number} · G12 · ✏️ Duy ve Yaz`
            : level === 5 ? ['', 'Tur 1 · Hece Köprüsü', 'Tur 2 · Kelime Fabrikası', 'Tur 3 · Sürpriz Kutusu', 'Tur 4 · L5-G04 · 🎈 Balon Patlat'][number]
            : level === 4 ? ['', 'Tur 1 · i sesi ve harfi', 'Tur 2 · l sesi ve harfi', 'Tur 3 · Sepet ve hafıza', 'Tur 4 · Hece', 'Tur 5 · Kelime ve final', 'Tur 6 · G11 · 🎈 Balon Patlat'][number]
            : level === 3 ? `Tur ${number} · L1 + L2 Karma`
            : level === 2 ? ['','Tur 1 · G01 / G02 · E','Tur 2 · G03 / G04 · T','Tur 3 · G05 / G06 · Sepet ve hafıza',
            'Tur 4 · G07 / G08 · Hece','Tur 5 · G09 / G10 · Kelime ve final', 'Tur 6 · G11 · 🎈 Balon Patlat', 'Tur 7 · G12 · ✏️ Duy ve Yaz'][number]
            : number === 1 ? 'Tur 1 · G01 / G02 · A' : number === 2 ? 'Tur 2 · G03 / G04 · N'
            : number === 3 ? 'Tur 3 · G05 / G06 · Ses seçimi'
            : number === 4 ? 'Tur 4 · G07 / G08 · Hafıza ve tren'
            : number === 5 ? 'Tur 5 · G09 / G10 · Hece ve final'
            : number === 6 ? 'Tur 6 · G11 · 🎈 Balon Patlat' : `Tur ${number}`}
        </MenuItem>)}
      </TextField>
      <Button variant="contained" disabled={!catalog || busy || (handwriting && !handwritingReady)} onClick={() => open('preview')}>Oyunu dene</Button>
      {catalog?.can_qa && catalog?.levels?.find((entry) => entry.level === level)?.can_qa !== false
        && (!handwriting || selectedLevel?.handwriting_preview?.can_qa)
        && !(((level === 1 && tour === 6) ||
          ([2,4,5].includes(level) && tour === catalog?.levels?.find(entry => entry.level === level)?.balloon_finale?.final_tour))
          && !catalog?.can_balloon_qa)
        && <Button variant="outlined" disabled={busy || (handwriting && !handwritingReady)} onClick={() => open('qa')}>İçeriği incele</Button>}
    </Stack>
    {handwriting && <Alert severity="info" sx={{ my: 2 }}>
      G12 · 2 hedef · {handwritingReady ? 'Onaylı Tomris kayıtları.' : 'Tomris kayıtları henüz doğrulanamadı; deneme kapalı.'} Bu deneme öğrenci sonucu veya içerik onayı kaydetmez.
      {level === 6 && ' Bu, beş karma + bir G12 döngüsündeki G12 örneğidir; öğrenci geçmişi kullanılmaz.'}
    </Alert>}
    {level === 1 && catalog?.balloon_finale && !catalog.balloon_finale.ready && (
      <Alert severity="info" sx={{ my: 2 }}>
        Balon Patlat final turu henüz ses doğrulamasını bekliyor. Tomris ses dosyaları
        henüz doğrulanmadığından deneme kapalı (eksik ses: {catalog.balloon_finale.missing_audio_ids?.length ?? 0}).
        Mevcut L1 oyunları ve öğrenci yayını etkilenmez.
      </Alert>
    )}
    {error && <Alert severity="error">{error}</Alert>}
    {round?.qa && handwriting && <Box component="section" sx={{ overflowWrap: 'anywhere', px: { xs: 0, md: 1 } }}>
      <HandwritingG12QaDetails round={round} />
      <Button onClick={exit}>İncelemeyi kapat</Button>
    </Box>}
    {round?.qa && !handwriting && <Box component="section" sx={{ overflowWrap: 'anywhere', '& audio': { maxWidth: '100%' } }}>
      <Alert severity="info">Dinleme ve glif kontrolü bekliyor. Bu ekran öğrenci sonucu veya QA onayı kaydetmez.</Alert>
      <Typography sx={{ my: 2 }}>İçerik sürümü: {round.content_version}</Typography>
      {round.qa.validators.map((check) => <Typography key={check.id}>{check.id}: {check.status}</Typography>)}
      {round.qa.games.map((game) => <Box key={game.game_id} sx={{ my: 3 }}>
        <Typography variant="h6">{game.source_game_id ? `L${game.source_level} · ${game.source_game_id}` : game.game_id} · Cevap anahtarı</Typography>
        {game.measurement_class && <Typography>Ölçüm sınıfı: {game.measurement_class} · MEB eşlemesi: {game.curriculum_mapping?.status || 'qa_required'}</Typography>}
        {game.reference && <Box sx={{ my: 2 }}><Typography>Referans · cevap seçeneği değildir</Typography>
          <img src={game.reference.image.url} alt={game.reference.canonical_name} width="90" /></Box>}
        {game.choices && <Stack direction="row" sx={{ my: 2, flexWrap: 'wrap', gap: 2 }}>
          {game.choices.map((choice) => <Box key={choice.id} sx={{ width: choice.id.length === 2 ? 120 : 60 }}>
            {choice.image ? <img src={choice.image.url} alt={choice.canonical_name} width="90" />
              : game.assessment_mode === 'balloon_pop' ? <Typography sx={{ fontWeight: 900 }}>{choice.id}</Typography>
              : choice.id.length === 2 ? <LetterSequence value={choice.id} size={70} /> : <Grade1Letter letter={choice.id} />}
          </Box>)}
        </Stack>}
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          {game.items.map((item) => <Box key={item.id} sx={{ my: 1, width: 110 }}>
            {game.assessment_mode === 'handwriting_dictation' ? <Typography sx={{ fontWeight: 900 }}>{item.expected_word?.toLocaleUpperCase('tr-TR')}</Typography>
              : item.image ? <img src={item.image.url} alt={item.canonical_name} width="90" />
              : item.is_target || item.glyph ? <Box sx={{ width: 60 }}>
                {item.letter || game.target_letter === 'e' || game.target_letter === 't'
                  ? <Grade1Letter letter={item.letter || game.target_letter} />
                  : item.glyph === 'school-n' || (item.is_target && game.target_letter === 'n') ? <SchoolN /> : <SingleStoreyA />}
              </Box> : <Typography>{item.symbol}</Typography>}
            <Typography>{item.canonical_name || item.id} · {item.expected_word ? `Hedef · ${item.audio_id}` : item.expected_id ? `→ ${item.expected_id}` : item.letter ? `Eş: ${item.letter}` : item.target_sequence ? `Sıra: ${item.target_sequence.join(' + ')}` : item.target_sound ? `→ ${item.target_sound}`
              : (item.is_target ?? item.contains_target_sound) ? 'Hedef' : 'Çeldirici'}</Typography>
            {item.stones && <Typography>Parçalar: {item.stones.map((stone) => stone.text || stone.letter).join(' · ')}</Typography>}
            {item.prefix !== undefined && <Typography>{item.prefix || '…'} + boşluk + {item.suffix || '…'}</Typography>}
          </Box>)}
        </Stack>
        {game.assessment_mode === 'silent_word_chest' && game.items.map((item) => <Box key={item.id} sx={{ my: 2 }}>
          <Typography>{item.id} · {item.skill_key}</Typography><LetterSequence value={item.written_word} size={60} />
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>{item.choices.map((choice) => <Box key={choice.id}>
            <img src={choice.image.url} alt={choice.semantic_label} width="90" /><Typography>{choice.semantic_label}</Typography>
          </Box>)}</Stack>
        </Box>)}
        {game.assessment_mode === 'mixed_final' && game.items.map((step) => <Box key={step.id} sx={{ my: 2 }}>
          <Typography>{step.id} · {step.skill_key} · {step.mechanic}{step.measurement_class ? ` · ${step.measurement_class}` : ''}</Typography>
          {step.reference && <Box><Typography>Referans</Typography><img src={step.reference.image.url} alt={step.reference.canonical_name || ''} width="90" /></Box>}
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>{step.items.map((item) => <Box key={item.id}>
            <Typography>{item.id}</Typography>
            {item.image && <img src={item.image.url} alt={item.canonical_name || ''} width="90" />}
            {item.letter && <Box sx={{ width: 60 }}><Grade1Letter letter={item.letter} /></Box>}
          </Box>)}</Stack>
          {step.written_word && <LetterSequence value={step.written_word} size={70} />}
          {step.mechanic === 'picture_word_choice' && <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            {step.allowed_ids.map((value) => <LetterSequence key={value} value={value} size={60} />)}
          </Stack>}
          {(step.choices || []).filter((choice) => choice.image).map((choice) => <img key={choice.id} src={choice.image.url} alt={choice.canonical_name} width="90" />)}
        </Box>)}
        {game.qa_audio.map((clip) => <Box key={clip.id} sx={{ my: 1 }}>
          <Typography>{isCorrectFeedback(clip.id) ? 'Doğru cevap sesi' : clip.text || clip.id}</Typography>
          {game.assessment_mode === 'handwriting_dictation' && <>
            <Typography>{clip.id} · Ses durumu: {clip.qa_status} · {clip.qa_review_id} · {clip.qa_approved_on}</Typography>
            <Typography>S3: {clip.s3_key}</Typography><Typography>SHA-256: {clip.sha256}</Typography>
            <Typography>Tomris: {clip.voice_id} · El yazısı/OCR: manuel inceleme bekliyor</Typography>
          </>}
          <audio controls preload="none" src={feedbackAudioUrl(clip.id, game.audio[clip.id])} onPlay={(event) => {
            const active = event.currentTarget;
            active.closest('section')?.querySelectorAll('audio').forEach((audio) => { if (audio !== active) audio.pause(); });
          }} />
        </Box>)}
      </Box>)}
      <Button onClick={exit}>İncelemeyi kapat</Button>
    </Box>}
  </Paper>;
}
